import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BiLogIn } from 'react-icons/bi';
import DropdownUser from '../DropdownUser';
import { useAuth } from '../../auth/context/AuthContext';
import logo from '../../static/logo.png';
import { PUBLIC_NAV_ABOUT, PUBLIC_NAV_MOBILE_FLAT, PUBLIC_NAV_PRODUCTS } from './publicNavBlueprint';
import { NavDropdownLink, NavMobileLink } from './PublicNavPrimitives';

const MIN_DROPDOWN_OPEN_MS = 2500;
const dropdownPanelBaseClass =
  'absolute left-0 top-full z-50 mt-2 w-52 rounded-lg border border-gray-200 bg-white p-2 shadow-xl transition dark:border-gray-700 dark:bg-boxdark';

export default function LandingHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'products' | 'about' | null>(null);
  const { user, logout } = useAuth();
  const dropdownOpenedAtRef = useRef<number | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleDropdownEnter = (menu: 'products' | 'about') => {
    clearCloseTimeout();
    setActiveDropdown((current) => {
      if (current !== menu) {
        dropdownOpenedAtRef.current = Date.now();
      }
      return menu;
    });
  };

  const handleDropdownLeave = (menu: 'products' | 'about') => {
    if (activeDropdown !== menu) {
      return;
    }
    const openedAt = dropdownOpenedAtRef.current ?? Date.now();
    const elapsed = Date.now() - openedAt;
    const remaining = Math.max(0, MIN_DROPDOWN_OPEN_MS - elapsed);

    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setActiveDropdown((current) => (current === menu ? null : current));
      closeTimeoutRef.current = null;
    }, remaining);
  };

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  return (
    <header className="absolute inset-x-0 top-0 z-50 bg-white bg-opacity-50 header-backdrop header-shadow dark:bg-boxdark-2 dark:border-b dark:border-gray-100/10">
      <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8" aria-label="Global">
        <div className="flex items-center">
          <Link to="/" className="flex items-center -m-1.5 p-1.5 text-gray-900 duration-300 ease-in-out hover:text-yellow-500">
            <img className="h-8 w-8" src={logo} alt="Pricing Table AI" />
            <span className="ml-2 text-sm font-semibold leading-6 dark:text-white">PricingTable AI</span>
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-white"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        </div>
        <div className="hidden lg:flex lg:gap-x-8">
          <div
            className="relative"
            onMouseEnter={() => handleDropdownEnter('products')}
            onMouseLeave={() => handleDropdownLeave('products')}
          >
            <button
              type="button"
              className="text-sm font-semibold leading-6 text-gray-900 dark:text-white duration-300 ease-in-out hover:text-yellow-500"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'products'}
            >
              Products
            </button>
            <div
              className={`${dropdownPanelBaseClass} ${
                activeDropdown === 'products' ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'
              }`}
            >
              {PUBLIC_NAV_PRODUCTS.map((item) => (
                <NavDropdownLink key={item.label} item={item} />
              ))}
            </div>
          </div>
          <div
            className="relative"
            onMouseEnter={() => handleDropdownEnter('about')}
            onMouseLeave={() => handleDropdownLeave('about')}
          >
            <button
              type="button"
              className="text-sm font-semibold leading-6 text-gray-900 dark:text-white duration-300 ease-in-out hover:text-yellow-500"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'about'}
            >
              About
            </button>
            <div
              className={`${dropdownPanelBaseClass} ${
                activeDropdown === 'about' ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'
              }`}
            >
              {PUBLIC_NAV_ABOUT.map((item) => (
                <NavDropdownLink key={item.label} item={item} />
              ))}
            </div>
          </div>
          <Link
            to="/purchase"
            className="text-sm font-semibold leading-6 text-sky-400 duration-300 ease-in-out hover:text-sky-300"
          >
            Purchase
          </Link>
        </div>
        <div className="hidden lg:flex gap-3 items-center">
          {user ? (
            <DropdownUser user={user} onLogout={logout} />
          ) : (
            <>
              <Link
                to="/signup"
                className="rounded-md bg-sky-400 px-4 py-2 text-sm font-semibold leading-6 text-white transition-colors duration-300 ease-in-out hover:bg-sky-300"
              >
                Sign Up
              </Link>
              <Link to="/login" className="text-sm font-semibold leading-6">
                <div className="flex items-center duration-300 ease-in-out text-gray-900 hover:text-yellow-500 dark:text-white">
                  Log in <BiLogIn size="1.1rem" className="ml-1 mt-[0.1rem]" />
                </div>
              </Link>
            </>
          )}
        </div>
      </nav>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white bg-opacity-95 header-backdrop dark:bg-boxdark-2 dark:bg-opacity-95 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5">
                <span className="text-2xl font-bold text-yellow-500">PricingTable AI</span>
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-gray-300"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {PUBLIC_NAV_MOBILE_FLAT.map((item) => (
                    <NavMobileLink key={item.label} item={item} onNavigate={() => setMobileMenuOpen(false)} />
                  ))}
                  <Link
                    to="/purchase"
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-sky-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Purchase
                  </Link>
                </div>
                <div className="py-6">
                  {user ? (
                    <div className="-mx-3 block rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                          {user.email}
                        </span>
                        <button
                          onClick={() => {
                            logout();
                            setMobileMenuOpen(false);
                          }}
                          className="text-base font-semibold leading-7 text-gray-900 dark:text-white hover:text-yellow-500"
                        >
                          Log out
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link
                        to="/signup"
                        className="-mx-3 block rounded-lg bg-sky-400 px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-sky-300"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Sign Up
                      </Link>
                      <Link
                        to="/login"
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div className="flex items-center">
                          Log in <BiLogIn size="1.1rem" className="ml-1" />
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
